import type {
    AdoptionRequirement,
    CompatibilityProfile,
    CompatibilityResult,
    CompatibilityVerdict,
    HousingType,
    ProfileQuestionId,
    RequirementCheck,
} from "../../compatibility/types.ts";
import { normalizeUsState } from "@wags/utils/us-states.ts";
import { clamp01 } from "../math.ts";

export type ScoreCompatibilityInput = {
    requirements: AdoptionRequirement[];
    profile: CompatibilityProfile;
    listingName?: string | null;
    organizationName?: string | null;
    organizationState?: string | null;
};

type Ctx = {
    dog: string;
    org: string;
    Org: string;
    orgState?: string;
};

function capitalizeFirst(s: string): string {
    return s.length ? s[0].toUpperCase() + s.slice(1) : s;
}

function housingWord(h: HousingType | undefined): string {
    switch (h) {
        case "apartment":
            return "an apartment";
        case "condo":
            return "a condo";
        case "townhouse":
            return "a townhouse";
        case "house":
            return "a house";
        default:
            return "your home";
    }
}

function housingIsHouse(h: HousingType | undefined): boolean | undefined {
    if (h === undefined) return undefined;
    return h === "house" || h === "townhouse";
}

function evaluateHomeRequirement(
    req: AdoptionRequirement,
    profile: CompatibilityProfile,
    ctx: Ctx,
): RequirementCheck {
    const { org, Org } = ctx;

    switch (req.kind) {
        case "house_required": {
            const isHouse = housingIsHouse(profile.housing);
            if (isHouse === true)
                return mk(
                    req,
                    "met",
                    `You have a house, which ${org} requires.`,
                );
            if (isHouse === false)
                return mk(
                    req,
                    "unmet",
                    `You mentioned you live in ${housingWord(profile.housing)}, and ${org} asks adopters to have a house.`,
                );
            return mk(
                req,
                "unknown",
                `${Org} asks adopters to have a house. What kind of home do you have?`,
                "housing",
            );
        }
        case "fenced_yard": {
            if (profile.fencedYard === true)
                return mk(
                    req,
                    "met",
                    `You have a fenced yard, which ${org} requires.`,
                );
            if (profile.fencedYard === false)
                return mk(
                    req,
                    "unmet",
                    `${Org} requires a fenced yard, and you mentioned you don't have one.`,
                );
            return mk(
                req,
                "unknown",
                `${Org} requires a fenced yard. Do you have one?`,
                "fencedYard",
            );
        }
        case "yard_required": {
            if (profile.hasYard === true)
                return mk(
                    req,
                    "met",
                    `You have a yard, which ${org} asks for.`,
                );
            if (profile.hasYard === false)
                return mk(
                    req,
                    "unmet",
                    `${Org} asks adopters to have a yard, and you mentioned you don't have one.`,
                );
            return mk(
                req,
                "unknown",
                `${Org} asks adopters to have a yard. Do you have one?`,
                "hasYard",
            );
        }
        case "home_ownership": {
            if (profile.ownsHome === true)
                return mk(
                    req,
                    "met",
                    `You own your home, which ${org} requires.`,
                );
            if (profile.ownsHome === false)
                return mk(
                    req,
                    "unmet",
                    `${Org} adopts to homeowners, and you mentioned you rent.`,
                );
            return mk(
                req,
                "unknown",
                `${Org} adopts to homeowners. Do you own your home?`,
                "ownsHome",
            );
        }
        default: {
            if (profile.ownsHome === true)
                return mk(
                    req,
                    "met",
                    `You own your home, so landlord approval isn't needed.`,
                );
            return mk(
                req,
                "info",
                `If you rent, ${org} will want your landlord's approval to keep a dog.`,
            );
        }
    }
}

function evaluateInStateRequirement(
    req: AdoptionRequirement,
    profile: CompatibilityProfile,
    ctx: Ctx,
): RequirementCheck {
    const { org, Org, orgState } = ctx;
    const userState = normalizeUsState(profile.state);
    const reqState = normalizeUsState(orgState);
    if (userState && reqState) {
        if (userState === reqState)
            return mk(
                req,
                "met",
                `You're in ${userState}, where ${org} adopts.`,
            );
        return mk(
            req,
            "unmet",
            `${Org} only adopts within ${reqState}, and you mentioned you're in ${userState}.`,
        );
    }
    if (!userState)
        return mk(
            req,
            "unknown",
            `${Org} only adopts to local homes${reqState ? ` in ${reqState}` : ""}. What state are you in?`,
            "state",
        );
    return mk(
        req,
        "unknown",
        `${Org} adopts to local homes only, but its state could not be confirmed.`,
    );
}

function evaluateHouseholdRequirement(
    req: AdoptionRequirement,
    profile: CompatibilityProfile,
    ctx: Ctx,
): RequirementCheck {
    const { dog, org, Org } = ctx;
    const soft = req.severity === "soft";

    switch (req.kind) {
        case "resident_dog_required": {
            if (profile.hasOtherDogs === true)
                return mk(
                    req,
                    "met",
                    `You already have a dog, which ${org} wants for ${dog}.`,
                );
            if (profile.hasOtherDogs === false)
                return mk(
                    req,
                    "unmet",
                    `${Org} wants ${dog} to join a home with another dog, and you mentioned you don't have one.`,
                );
            return mk(
                req,
                "unknown",
                `${Org} wants ${dog} to have another dog at home. Do you have one?`,
                "hasOtherDogs",
            );
        }
        case "only_dog": {
            const verb = soft ? "may do best as" : "needs to be";
            if (profile.hasOtherDogs === false)
                return mk(
                    req,
                    "met",
                    `${capitalizeFirst(dog)} ${verb} the only dog, and you don't have others.`,
                );
            if (profile.hasOtherDogs === true)
                return mk(
                    req,
                    "unmet",
                    `${capitalizeFirst(dog)} ${verb} the only dog, and you mentioned you have another.`,
                );
            return mk(
                req,
                "unknown",
                `${capitalizeFirst(dog)} ${verb} the only dog. Do you have other dogs?`,
                "hasOtherDogs",
            );
        }
        case "no_cats": {
            const phrase = soft
                ? "may not be comfortable around cats"
                : "can't live with cats";
            if (profile.hasCats === false)
                return mk(
                    req,
                    "met",
                    `${capitalizeFirst(dog)} ${phrase}, and you don't have any.`,
                );
            if (profile.hasCats === true)
                return mk(
                    req,
                    "unmet",
                    `${capitalizeFirst(dog)} ${phrase}, and you mentioned you have one.`,
                );
            return mk(
                req,
                "unknown",
                `${capitalizeFirst(dog)} ${phrase}. Do you have a cat?`,
                "hasCats",
            );
        }
        case "no_young_children": {
            const phrase = soft
                ? "may not suit a home with young children"
                : "needs a home without young children";
            if (profile.hasYoungKids === false)
                return mk(
                    req,
                    "met",
                    `No young kids at home, which works since ${dog} ${phrase}.`,
                );
            if (profile.hasYoungKids === true)
                return mk(
                    req,
                    "unmet",
                    `${capitalizeFirst(dog)} ${phrase}, and you mentioned you have young kids.`,
                );
            return mk(
                req,
                "unknown",
                `${capitalizeFirst(dog)} ${phrase}. Any young kids at home?`,
                "hasYoungKids",
            );
        }
        default: {
            if (profile.experiencedOwner === true)
                return mk(
                    req,
                    "met",
                    `You've owned dogs before, which ${org} prefers.`,
                );
            if (profile.experiencedOwner === false)
                return mk(
                    req,
                    "unmet",
                    `${Org} prefers an experienced owner, and you mentioned this is your first dog.`,
                );
            return mk(
                req,
                "unknown",
                `${Org} prefers an experienced owner. Have you owned a dog before?`,
                "experiencedOwner",
            );
        }
    }
}

function evaluateCheck(
    req: AdoptionRequirement,
    profile: CompatibilityProfile,
    ctx: Ctx,
): RequirementCheck {
    switch (req.kind) {
        case "house_required":
        case "fenced_yard":
        case "yard_required":
        case "home_ownership":
        case "landlord_approval":
            return evaluateHomeRequirement(req, profile, ctx);
        case "in_state_only":
            return evaluateInStateRequirement(req, profile, ctx);
        case "resident_dog_required":
        case "only_dog":
        case "no_cats":
        case "no_young_children":
        case "experienced_owner":
            return evaluateHouseholdRequirement(req, profile, ctx);
    }
}

function mk(
    requirement: AdoptionRequirement,
    status: RequirementCheck["status"],
    explanation: string,
    question?: RequirementCheck["question"],
): RequirementCheck {
    return { requirement, status, explanation, question };
}

function pickVerdict(args: {
    requirementCount: number;
    hardUnmet: number;
    hardUnknown: number;
    softUnmet: number;
    anyUnknown: boolean;
}): CompatibilityVerdict {
    const { requirementCount, hardUnmet, hardUnknown, softUnmet, anyUnknown } =
        args;
    if (hardUnmet > 0) return "mismatch";
    if (hardUnknown > 0) return "needs_info";
    if (softUnmet > 0) return "review";
    if (requirementCount === 0) return "great";
    return anyUnknown ? "good" : "great";
}

function summarize(
    verdict: CompatibilityVerdict,
    ctx: Ctx,
    requirementCount: number,
    actionableCount: number,
): string {
    switch (verdict) {
        case "great":
            if (requirementCount === 0)
                return "No special adoption requirements listed. Looks like a straightforward fit.";
            if (actionableCount === 0)
                return "No requirements that depend on your home.";
            return `You meet everything ${ctx.org} asks for.`;
        case "good":
            return "You clear the main requirements. A couple of things are left to confirm.";
        case "needs_info":
            return "A few quick questions will show whether you're eligible.";
        case "review":
            return "Mostly a fit. One thing is worth a closer look.";
        case "mismatch":
            return `${ctx.Org} has a requirement your home may not meet.`;
    }
}

export function scoreCompatibility(
    input: ScoreCompatibilityInput,
): CompatibilityResult {
    const org = input.organizationName?.trim() || "this source";
    const ctx: Ctx = {
        dog: input.listingName?.trim() || "this dog",
        org,
        Org: capitalizeFirst(org),
        orgState: input.organizationState ?? undefined,
    };

    const checks = input.requirements.map((req) =>
        evaluateCheck(req, input.profile, ctx),
    );

    const met = checks.filter((c) => c.status === "met");
    const blockers = checks.filter((c) => c.status === "unmet");
    const unknowns = checks.filter((c) => c.status === "unknown");
    const info = checks.filter((c) => c.status === "info");

    const hardUnmet = blockers.filter(
        (c) => c.requirement.severity === "hard",
    ).length;
    const softUnmet = blockers.length - hardUnmet;
    const hardUnknown = unknowns.filter(
        (c) => c.requirement.severity === "hard",
    ).length;

    let score = 1;
    score -= softUnmet * 0.18;
    if (hardUnmet > 0) score = Math.min(score, 0.2) - (hardUnmet - 1) * 0.05;
    score = clamp01(score) ?? 0;

    const verdict = pickVerdict({
        requirementCount: input.requirements.length,
        hardUnmet,
        hardUnknown,
        softUnmet,
        anyUnknown: unknowns.length > 0,
    });

    return {
        score,
        verdict,
        confidence: unknowns.length > 0 ? "low" : "high",
        checks,
        met,
        blockers,
        unknowns,
        info,
        summary: summarize(
            verdict,
            ctx,
            input.requirements.length,
            met.length + blockers.length + unknowns.length,
        ),
    };
}

export function relevantQuestions(
    result: CompatibilityResult,
): ProfileQuestionId[] {
    const ordered = [...result.unknowns].sort(
        (a, b) =>
            (a.requirement.severity === "hard" ? 0 : 1) -
            (b.requirement.severity === "hard" ? 0 : 1),
    );
    const ids: ProfileQuestionId[] = [];
    const seen = new Set<ProfileQuestionId>();
    for (const check of ordered) {
        if (check.question && !seen.has(check.question)) {
            seen.add(check.question);
            ids.push(check.question);
        }
    }
    return ids;
}
